'use client';

// @spec authentication-pwa-install
import { detectInstallSurface, type InstallSurface } from '@client/pwa-detection';
import { useAuth } from '@client/services/auth-provider';
import { isStandaloneDisplayMode } from '@fpo3/ts-commons-ui';
import { Download, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}
const DISMISS_KEY = 'famfam:pwa-install-banner-dismissed:v1';
const INSTALLED_KEY = 'famfam:pwa-install-banner-installed:v1';
export function PwaInstallBanner() {
  const {
    status
  } = useAuth();
  const pathname = usePathname();
  const [isReady, setIsReady] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [installSurface, setInstallSurface] = useState<InstallSurface>('unsupported');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isPrompting, setIsPrompting] = useState(false);
  useEffect(() => {
    const mediaQuery = globalThis.matchMedia('(display-mode: standalone)');
    const updateInstalledState = () => {
      const knownInstalled = globalThis.localStorage.getItem(INSTALLED_KEY) === '1';
      setIsInstalled(knownInstalled || isStandaloneDisplayMode());
    };
    const dismissed = globalThis.localStorage.getItem(DISMISS_KEY) === '1';
    /* eslint-disable react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-initialize-state -- intentional: reads localStorage and browser APIs not available during SSR */
    setIsDismissed(dismissed);
    setInstallSurface(detectInstallSurface());
    updateInstalledState();
    setIsReady(true);
    /* eslint-enable react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-initialize-state */

    mediaQuery.addEventListener('change', updateInstalledState);
    return () => mediaQuery.removeEventListener('change', updateInstalledState);
  }, []);
  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setDeferredPrompt(installEvent);
    };
    const handleAppInstalled = () => {
      globalThis.localStorage.setItem(INSTALLED_KEY, '1');
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    globalThis.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    globalThis.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      globalThis.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      globalThis.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  const dismissBanner = () => {
    globalThis.localStorage.setItem(DISMISS_KEY, '1');
    setIsDismissed(true);
  };
  const markInstalled = () => {
    globalThis.localStorage.setItem(INSTALLED_KEY, '1');
    setIsInstalled(true);
  };
  const installApp = async () => {
    if (!deferredPrompt) {
      if (installSurface === 'chromium') {
        globalThis.alert("Chrome didn't expose the install prompt for this tab yet.\nUse Chrome menu (⋮) > Cast, save, and share > Install page as app.");
      }
      return;
    }
    setIsPrompting(true);
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } finally {
      setIsPrompting(false);
    }
  };
  const showBrowserInstructions = installSurface === 'chromium' || installSurface === 'safari-macos' || installSurface === 'safari-ios';
  const showChromiumManualInstructions = installSurface === 'chromium' && deferredPrompt == null;
  const shouldShowInstallAction = deferredPrompt != null || installSurface === 'chromium';
  if (!isReady || status !== 'authenticated') {
    return null;
  }
  if (pathname?.startsWith('/dev')) {
    return null;
  }
  if (isInstalled || isDismissed) {
    return null;
  }
  if (!shouldShowInstallAction && !showBrowserInstructions) {
    return null;
  }
  return <div className="bg-sky-600 text-white px-4 py-2 text-sm">
      <div className="mx-auto flex w-full max-w-5xl items-center gap-3">
        <Download className="h-4 w-4 shrink-0" />

        <div className="min-w-0 flex-1">
          <p className="font-medium">Install Famfam</p>
          {shouldShowInstallAction && <p className="text-sky-50/90">
              Get a faster launch experience from your dock or home screen.
            </p>}
          {showChromiumManualInstructions && <p className="text-sky-50/90">
              Chrome: click the install icon in the address bar (or menu &gt; Cast, save, and share
              &gt; Install page as app).
            </p>}
          {!shouldShowInstallAction && installSurface === 'safari-macos' && <p className="text-sky-50/90">Safari: File &gt; Add to Dock.</p>}
          {!shouldShowInstallAction && installSurface === 'safari-ios' && <p className="text-sky-50/90">Safari: Share menu &gt; Add to Home Screen.</p>}
        </div>

        <div className="flex items-center gap-2">
          {shouldShowInstallAction && <Button variant="secondary" size="sm" onClick={installApp} disabled={isPrompting} className="h-8 bg-white text-sky-700 hover:bg-sky-50">
              {isPrompting ? 'Installing...' : 'Install'}
            </Button>}
          {showChromiumManualInstructions && <Button variant="ghost" size="sm" onClick={markInstalled} className="h-8 text-white hover:bg-sky-500 hover:text-white">
              I installed it
            </Button>}
          <Button variant="ghost" size="icon-sm" onClick={dismissBanner} className="h-8 w-8 text-white hover:bg-sky-500 hover:text-white" aria-label="Dismiss install banner">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>;
}